import { AppDataActions } from '../redux/app-data';
import { DataConvertor } from '@888sport/uc-sportclient-lib/dist/uc-sportclient-lib.min';
import FavoritesApi from '../services/favorites-api/favorites-api';
import { GetKambiApi } from '../services/index';
import Global from '../global';
import Socket from '../services/kambi-push-api/kambi-push-api';
import WidgetApi from '../services/kambi-widget-api';
import { getPushAPI } from '../config/kambi/api-config/kambi-config';
import getPushApiCallbacks from '../services/kambi-push-api/push-api-callbacks';
import { getTopicsToSubscribe } from '../services/kambi-push-api/push-api-configs';
import unionBy from 'lodash/unionBy';

class ClientIntegration {
  static performLogin(userContext, initialSportClientToken) {
    return WidgetApi.get().loginAsync(userContext, initialSportClientToken);
  }

  static extendSession(userContext, useProdApis = false) {
    return GetKambiApi()
      .extendSession(userContext, useProdApis)
      .then(response =>
        new DataConvertor(Global.logger).transformSessionExtend(response)
      );
  }

  static loadDataForCashoutableBetsStrip(userContext, useProdApis = false) {
    let promises = [
      GetKambiApi().getOpenBets(userContext, useProdApis),
      GetKambiApi().getLiveEvents(userContext, useProdApis),
      GetKambiApi().getCashOutBets(userContext, useProdApis)
    ];
    return Promise.all(promises).then(values =>
      new DataConvertor(Global.logger).transformCashoutableBets(values)
    );
  }

  static makeCashIn(payload, useProdApis = false) {
    const { singleBetId } = payload;
    return GetKambiApi()
      .makeCashInRequest(payload, useProdApis)
      .then(response =>
        new DataConvertor(Global.logger).transformCashInBet(
          response,
          singleBetId
        )
      );
  }

  static initSportProviderSocket(store) {
    if (Global.config.environment !== 'production') {
      window.processMsg = msg => {
        const messageInfo = JSON.parse(msg);
        store.dispatch(AppDataActions.processMessagesFromWS(messageInfo));
      };
    }

    let state = store.getState();
    const { useProdApis } = state.appConfig;
    const {
      offering,
      market,
      locale,
      countryISO3,
      currency,
      langISO2,
      brand,
      subBrand,
      isLoggedIn
    } = state.userContext;

    const logData = {
      brand,
      countryISO3,
      currency,
      isMobileLook: state.appUI.isMobileLook,
      langISO2,
      locale,
      market,
      offering,
      subBrand,
      tokensFromWrapper: isLoggedIn,
      wsUrl: getPushAPI(useProdApis)
    };
    const options = getPushApiCallbacks(store, logData);

    Socket.destroy();
    const SocketClient = Socket.getInstance(options);
    const topics = getTopicsToSubscribe(state);
    SocketClient.openSocket();
    SocketClient.subscribeToTopics(topics);
  }

  static getBetOfferOutcome(selectionId, userContext, useProdApis = false) {
    return GetKambiApi()
      .getBetOfferOutcome(selectionId, userContext, useProdApis)
      .then(res => res)
      .catch(err => {
        Global.logger.error(`getBetOfferOutcome ${err}`);
        return null;
      });
  }

  static getLiveRightNowStripData(userContext, useProdApis = false) {
    return GetKambiApi()
      .getLiveEvents(userContext, useProdApis)
      .then(res =>
        new DataConvertor(Global.logger).transformLiveRightNowStripData(res)
      )
      .catch(err => {
        Global.logger.error(`getLiveRightNowStripData ${err}`);
        return [];
      });
  }

  static getEventsWithBetOffersHighlightsStrip(
    sportGroups,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    const groupIds = sportGroups.map(group => group.id);
    return GetKambiApi()
      .getEventsForHighlightsStrip(
        groupIds,
        locale,
        market,
        offering,
        logData,
        useProdApis
      )
      .then(res =>
        new DataConvertor(Global.logger).transformHighlightsStripData(
          res,
          sportGroups
        )
      )
      .catch(err => {
        Global.logger.error(`getEventsWithBetOffersHighlightsStrip ${err}`);
        return null;
      });
  }

  /**
   * Gets bet offers for specified event. Type can be used to filter the bet offers by bet offer type.
   * @param {number[]} eventIds - Array of event IDs to get.
   * @param {string} locale - Client locale.
   * @param {string} market - Client market.
   * @param {string} offering - Client offering.
   * @param {number} type - Bet offer type.
   * @param {any} logData - log data TODO needs an object type
   * @param {boolean} useProdApis - Uses prod api when true;
   * @return {Promise<KBetOfferResponse>} - Events and bet offers array
   */
  static getBetOffersForEvents(
    eventIds,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    return GetKambiApi()
      .getBetOffersForEvents(
        eventIds,
        locale,
        market,
        offering,
        null,
        logData,
        useProdApis
      )
      .then(data =>
        new DataConvertor(Global.logger).transformBetOffersForEventIdsResponse(
          data
        )
      );
  }

  static getListViewData(
    groupId,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    let getArrayOfRemainingCalls = (
      range,
      groupId,
      locale,
      market,
      offering,
      logData,
      useProdApis = false
    ) => {
      const { total, size } = range;
      const remainingRequestsToDo = Math.trunc(total / size);
      let requests = [],
        range_start = size;

      for (let i = 0; i < remainingRequestsToDo; i++) {
        requests.push(
          GetKambiApi().getListViewData(
            groupId,
            locale,
            market,
            offering,
            logData,
            range_start,
            useProdApis
          )
        );
        range_start = range_start + size;
      }

      return requests;
    };

    return GetKambiApi()
      .getListViewData(
        groupId,
        locale,
        market,
        offering,
        logData,
        0,
        useProdApis
      )
      .then(firstRes => {
        if (
          firstRes &&
          firstRes.range &&
          firstRes.range.total > firstRes.range.size
        ) {
          //TODO[EV]: On this Promise.all we could refactor the promise array so that if one request from that array
          //TODO[EV]-> drops we will still get the response for the other request that were successfully made/completed.
          return Promise.all(
            getArrayOfRemainingCalls(
              firstRes.range,
              groupId,
              locale,
              market,
              offering,
              logData,
              useProdApis
            )
          )
            .then(responses => {
              let mergedResponse = {
                betOffers: firstRes.betOffers || [],
                events: firstRes.events || []
              };
              responses.forEach(res => {
                if (res.betOffers && res.events) {
                  //merge responses with no duplicate events/betoffers - unique by id
                  mergedResponse.betOffers = unionBy(
                    mergedResponse.betOffers,
                    res.betOffers,
                    'id'
                  );
                  mergedResponse.events = unionBy(
                    mergedResponse.events,
                    res.events,
                    'id'
                  );
                }
              });
              return new DataConvertor(Global.logger).transformListViewData(
                mergedResponse
              );
            })
            .catch(() =>
              new DataConvertor(Global.logger).transformListViewData(firstRes)
            );
        } else {
          return new DataConvertor(Global.logger).transformListViewData(
            firstRes
          );
        }
      })
      .catch(err => {
        Global.logger.error(`getListViewData ${err}`);
        return [];
      });
  }

  static getEventDetails(
    eventId,
    locale,
    market,
    offering,
    logData,
    useProdApis = false,
    shouldConvertData = true
  ) {
    return GetKambiApi()
      .getEventDetails(eventId, locale, market, offering, logData, useProdApis)
      .then(response =>
        shouldConvertData
          ? new DataConvertor(Global.logger).transformEventDetails(response)
          : response
      );
  }

  /**
   * Get live events.
   * @memberOf KambiApi
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {*} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<ILiveEventResponse | {}>}
   */
  static getLiveEventsStripData(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return GetKambiApi()
      .getLiveEvents(locale, market, offering, logData, useProdApis)
      .then(res =>
        new DataConvertor(Global.logger).transformLiveRightNowStripData(res)
      )
      .catch(() => {
        return {};
      });
  }

  static getLiveEvents(userContext) {
    return GetKambiApi()
      .getLiveEvents(userContext)
      .then(response =>
        new DataConvertor(Global.logger).transformLiveEvents(response)
      )
      .catch(() => []);
  }

  static getEnhancedSpecials(
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return GetKambiApi()
      .getEnhancedSpecials(locale, market, offering, logData, useProdApis)
      .then(res =>
        new DataConvertor(Global.logger).transformEnhancedSpecialsForStrip(res)
      )
      .catch(() => null);
  }

  /**
   * Gets events and bet offers by event ID array.
   * @param {number[]} eventIds - Array of event IDs to get.
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {any} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {{outcomesMap: IOutcomesMap, betOffersMap: IBetOffersMap, eventsMap: IEventsMap, participantsMap: IParticipantsMap}}
   */
  static async getEventsAndBetOffersByEventIds(
    eventIds,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    const [
      { betOffers = [], events = [] },
      { events: eventsWithParticipants = [] }
    ] = [
      await GetKambiApi()
        .getBetOffersForEvents(
          eventIds,
          locale,
          market,
          offering,
          4,
          logData,
          useProdApis
        )
        .catch(() => {
          return {};
        }),
      await GetKambiApi()
        .getEventsWithIcons(eventIds, offering, logData, useProdApis)
        .catch(() => {
          return {};
        })
    ];

    return new DataConvertor(Global.logger).transformNextSportCategoryData(
      events,
      betOffers,
      eventsWithParticipants
    );
  }

  /**
   * Get meetings for sport category
   * @param {string} sportCategory - Code for sports. Example: horse_racing
   * @param {string} locale - Defines what language should be used for localized texts. Example en_GB, fr_FR.
   * @param {string} market - Defines the geographical market that the player is registered in. Example GB, FR.
   * @param {string} offering - Unique identifier assigned to the API user. Example: kambi.
   * @param {any} logData - log data TODO needs an object type.
   * @param {boolean} [useProdApis=false] - Uses prod API when true.
   * @return {Promise<KMeeting[]>} - Array of meetings that contain racing events.
   */
  static getMeetingsForSportCategory(
    sportCategory,
    locale,
    market,
    offering,
    logData,
    useProdApis = false
  ) {
    return GetKambiApi().getMeetingsForSportCategory(
      sportCategory,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
  }

  /**
   * Get next events for sport category
   * @param {string} sportCategory
   * @param {string} locale
   * @param {string} market
   * @param {string} offering
   * @param {Object} logData
   * @param {boolean} useProdApis
   * @return {Promise<*>}
   */
  static async getNextEventsForSportCategory(
    sportCategory,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    const meetings = await ClientIntegration.getMeetingsForSportCategory(
      sportCategory,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );
    return meetings
      .reduce((arr, meeting) => arr.concat(meeting.events), [])
      .filter(event => event.state === 'NOT_STARTED')
      .sort(
        (event1, event2) =>
          new Date(event1.startTime).getTime() -
          new Date(event2.startTime).getTime()
      )
      .map(event => ({ id: event.id, startTime: event.startTime }));
  }

  /**
   *
   * @param {string} sportCategory
   * @param {number} eventCount
   * @param {string} locale
   * @param {string} market
   * @param {string} offering
   * @param {Object} logData
   * @param {boolean} useProdApis
   * @return {Promise<*>}
   */
  static async getNextSportCategory(
    sportCategory,
    eventCount,
    locale,
    market,
    offering,
    logData,
    useProdApis
  ) {
    const allEvents = await ClientIntegration.getNextEventsForSportCategory(
      sportCategory,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );

    if (!allEvents.length) {
      Global.logger.debug('No horse races events available');
      return {
        allEvents: [],
        entities: {
          participantsMap: {},
          eventsMap: {},
          outcomesMap: {},
          betOffersMap: {}
        }
      };
    }

    const eventIds = allEvents.slice(0, eventCount).map(event => event.id);
    const entities = await ClientIntegration.getEventsAndBetOffersByEventIds(
      eventIds,
      locale,
      market,
      offering,
      logData,
      useProdApis
    );

    return {
      allEvents,
      ...entities
    };
  }

  static getHighlights(locale, market, offering, logData, useProdApis = false) {
    return GetKambiApi()
      .getEventsForHighlights(locale, market, offering, logData, useProdApis)
      .then(response =>
        new DataConvertor(Global.logger).transformEventsForHighlights(response)
      )
      .then(eventsList => {
        return GetKambiApi()
          .getBetOffersForHighlights(
            eventsList,
            locale,
            market,
            offering,
            logData,
            useProdApis
          )
          .then(response =>
            new DataConvertor(Global.logger).transformBetOffersForHighlights(
              response
            )
          )
          .catch(err => {
            Global.logger.error(`getBetOffersForHighlights ${err}`);
            return [];
          });
      })
      .catch(err => {
        Global.logger.error(`getEventsForHighlights ${err}`);
        return [];
      });
  }

  static getFavoritesSearchResults(
    locale,
    market,
    offering,
    searchTerm,
    logData,
    useProdApis
  ) {
    return GetKambiApi()
      .getSearchResults(
        locale,
        market,
        offering,
        searchTerm,
        logData,
        useProdApis
      )
      .then(response =>
        new DataConvertor(Global.logger).transformFavoritesSearchResults(
          response
        )
      )
      .catch(err => {
        Global.logger.error(`getSearchResults ${err}`);
        return [];
      });
  }

  static validateAndGetFavoritesInitialData(logData, token, useProdApis) {
    return FavoritesApi.validateToken(logData, token, useProdApis)
      .then(tokenData => {
        const { token: favoriteToken } = tokenData;
        return FavoritesApi.getInitialData(logData, favoriteToken, useProdApis)
          .then(res => {
            return { initialData: res, favoriteToken };
          })
          .catch(err => {
            Global.logger.error(`[favor8] getFavoritesInitialData ${err}`);
            return [];
          });
      })
      .catch(err => {
        Global.logger.error(
          `[favor8] validateAndGetFavoritesInitialData ${err}`
        );
        return [];
      });
  }

  static setComingSoon(logData, notified, token, useProdApis) {
    return FavoritesApi.setComingSoon(logData, notified, token, useProdApis)
      .then(data => {
        if (data) {
          return data;
        } else {
          Global.logger.error(`[favor8] setComingSoon ${data.err}`);
        }
      })
      .catch(err => {
        Global.logger.error(`[favor8] setComingSoon ${err}`);
        return [];
      });
  }

  static setEndgame(logData, notified, token, useProdApis) {
    return FavoritesApi.setEndgame(logData, notified, token, useProdApis)
      .then(data => {
        if (data) {
          return data;
        } else {
          Global.logger.error(`[favor8] setEndgame ${data.err}`);
        }
      })
      .catch(err => {
        Global.logger.error(`[favor8] setEndgame ${err}`);
        return [];
      });
  }

  static followFavorite(
    logData,
    favoriteToken,
    sportType,
    itemName,
    locale,
    market,
    offering,
    useProdApis
  ) {
    return FavoritesApi.followFavorite(
      logData,
      favoriteToken,
      sportType,
      itemName,
      locale,
      market,
      offering,
      useProdApis
    ).catch(err => {
      Global.logger.error(`[favor8] followFavorite ${err}`);
    });
  }

  static unfollowFavorite(
    logData,
    favoriteToken,
    sportType,
    itemName,
    useProdApis
  ) {
    return FavoritesApi.unfollowFavorite(
      logData,
      favoriteToken,
      sportType,
      itemName,
      useProdApis
    ).catch(err => {
      Global.logger.error(`[favor8] unfollowFavorite ${err}`);
    });
  }
}

export default ClientIntegration;
